import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Video, VideoOff, Mic, MicOff, Settings, Check, Clock, ArrowLeft } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { useMediaPrewarm } from '@/features/videocall/components/hooks/useMediaPrewarm';
import { useWebViewViewportHeight } from '@/shared/hooks/useWebViewViewportHeight';
import { supabase } from '@/integrations/supabase/client';

export default function PreJoinLobby() {
  const { roomId } = useParams<{ roomId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  
  const [displayName, setDisplayName] = useState('');
  const [last4Digits, setLast4Digits] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [guestToken, setGuestToken] = useState<string>(''); // JWT token for guest authentication
  const [isVerifying, setIsVerifying] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [devices, setDevices] = useState<{
    videoDevices: MediaDeviceInfo[];
    audioDevices: MediaDeviceInfo[];
  }>({ videoDevices: [], audioDevices: [] });
  const [selectedVideoDevice, setSelectedVideoDevice] = useState('');
  const [selectedAudioDevice, setSelectedAudioDevice] = useState('');
  const [showDeviceSettings, setShowDeviceSettings] = useState(false);
  const [mediaReady, setMediaReady] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isTooEarly, setIsTooEarly] = useState(false);
  const [autoStartRecording, setAutoStartRecording] = useState(false); // Recording toggle for host
  const [cameraFacingMode, setCameraFacingMode] = useState<'user' | 'environment'>('user'); // Track which camera is active

  // Check if user is host (has ?host=1 in URL)
  const isHost = searchParams.get('host') === '1';

  // ✅ Detect if running in WebView (mobile app)
  const isWebView = () => {
    const ua = navigator.userAgent;
    if (typeof (window as any).ReactNativeWebView !== 'undefined') return true;
    if (ua.includes('WlkthruMobileApp')) return true;
    if ((window as any).__IS_MOBILE_WEBVIEW__ === true) return true;
    return false;
  };

  // WebView viewport height hook (sets --app-vh CSS variable)
  useWebViewViewportHeight();

  // ✅ FIX: Auto-skip prejoin for host rejoins ONLY (browser hosts, NOT WebView)
  // Hosts are authenticated users and don't need to go through prejoin on rejoin
  // WebView hosts should ALWAYS see prejoin lobby for camera/mic setup and terms acceptance
  useEffect(() => {
    if (!roomId || !isHost) return;

    // ✅ CRITICAL: WebView hosts should NEVER auto-skip prejoin lobby
    // Mobile app users need to see the prejoin lobby for:
    // - Camera/mic preview and toggle
    // - Device selection
    // - Terms acceptance
    // - Auto-record toggle for hosts
    if (isWebView()) {
      console.log('[PreJoinLobby] WebView host detected - showing prejoin lobby');
      // Clear any stale session flag to prevent issues
      sessionStorage.removeItem(`joined_${roomId}`);
      return; // Don't auto-skip for WebView hosts
    }

    // Check if this is a rejoin (using sessionStorage to track) - browser hosts only
    const hasJoinedBefore = sessionStorage.getItem(`joined_${roomId}`);

    if (hasJoinedBefore) {
      console.log('[PreJoinLobby] Browser host rejoin detected - skipping prejoin, going straight to call');
      // Navigate directly to call with minimal prejoin state
      navigate(`/video-call/${roomId}`, {
        state: {
          prejoin: {
            displayName: 'Host',
            micOn: true,
            camOn: true,
            timestamp: Date.now(),
            isHost: true,
          },
        },
        replace: true,
      });
    }
  }, [roomId, isHost, navigate]);

  // Check if call is scheduled and if user is too early
  // REMOVED: Direct Supabase call - this should be done via backend API
  // The backend API should handle call timing validation
  // For now, we'll skip this check to prevent direct DB queries
  useEffect(() => {
    // TODO: Implement backend API endpoint for call timing validation
    // Example: GET /calls/:roomId/timing
    // This endpoint should return { scheduled_start_time, is_too_early, can_join }

    // For now, we assume the call is always joinable
    setIsTooEarly(false);
  }, [roomId]);

  // Pre-warm media devices
  useMediaPrewarm({ video: true, audio: true });

  // Get available devices
  useEffect(() => {
    const getDevices = async () => {
      try {
        const deviceList = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = deviceList.filter(d => d.kind === 'videoinput');
        const audioDevices = deviceList.filter(d => d.kind === 'audioinput');
        
        setDevices({ videoDevices, audioDevices });
        
        if (videoDevices.length > 0 && !selectedVideoDevice) {
          setSelectedVideoDevice(videoDevices[0].deviceId);
        }
        if (audioDevices.length > 0 && !selectedAudioDevice) {
          setSelectedAudioDevice(audioDevices[0].deviceId);
        }
      } catch (err) {
        console.error('Failed to enumerate devices:', err);
      }
    };

    getDevices();
  }, [selectedVideoDevice, selectedAudioDevice]);

  // Initialize preview
  useEffect(() => {
    let mounted = true;

    const initPreview = async () => {
      try {
        setIsLoading(true);

        const constraints: MediaStreamConstraints = {
          video: camOn ? (selectedVideoDevice
            ? { deviceId: { exact: selectedVideoDevice } }
            : { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } }) : false,
          audio: micOn ? (selectedAudioDevice
            ? { deviceId: { exact: selectedAudioDevice } }
            : { echoCancellation: true, noiseSuppression: true }) : false,
        };

        // Try to get media, but be graceful if one fails
        let stream: MediaStream | null = null;

        try {
          stream = await navigator.mediaDevices.getUserMedia(constraints);
        } catch (err: any) {
          console.warn('Failed to get media with both audio and video:', err);

          // Try video only
          if (constraints.video) {
            try {
              stream = await navigator.mediaDevices.getUserMedia({ video: constraints.video, audio: false });
              console.log('✅ Got video-only stream');
              setError('Microphone access denied. Continuing with video only.');
            } catch (videoErr) {
              console.warn('Video-only also failed:', videoErr);
            }
          }

          // Try audio only if video failed
          if (!stream && constraints.audio) {
            try {
              stream = await navigator.mediaDevices.getUserMedia({ video: false, audio: constraints.audio });
              console.log('✅ Got audio-only stream');
              setError('Camera access denied. Continuing with audio only.');
            } catch (audioErr) {
              console.warn('Audio-only also failed:', audioErr);
              throw err; // Re-throw original error
            }
          }

          if (!stream) {
            throw err; // Re-throw if both failed
          }
        }
        
        if (!mounted) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }

        streamRef.current = stream;

        // Only try to play video if stream has video tracks
        const hasVideo = stream.getVideoTracks().length > 0;

        // Detect camera facing mode from stream
        if (hasVideo) {
          const videoTrack = stream.getVideoTracks()[0];
          const settings = videoTrack.getSettings();
          const facingMode = settings.facingMode as 'user' | 'environment' | undefined;

          if (facingMode) {
            console.log('📷 [PreJoin] Detected camera facing mode:', facingMode);
            setCameraFacingMode(facingMode);
          } else {
            // Fallback: check track label
            const label = videoTrack.label.toLowerCase();
            if (label.includes('back') || label.includes('rear') || label.includes('environment')) {
              console.log('📷 [PreJoin] Inferred back camera from label');
              setCameraFacingMode('environment');
            } else {
              console.log('📷 [PreJoin] Defaulting to front camera');
              setCameraFacingMode('user');
            }
          }
        }

        if (videoRef.current && hasVideo) {
          videoRef.current.srcObject = stream;

          // ✅ CRITICAL: Must configure BEFORE play attempt
          videoRef.current.muted = true; // Required for iOS Safari autoplay
          videoRef.current.playsInline = true;

          // ✅ CRITICAL: Wait for metadata before playing
          if (videoRef.current.readyState < 2) {
            try {
              await new Promise((resolve, reject) => {
                const timeout = setTimeout(() => reject(new Error('Metadata timeout')), 3000);
                videoRef.current!.addEventListener('loadedmetadata', () => {
                  clearTimeout(timeout);
                  resolve(true);
                }, { once: true });
              });
            } catch (metaErr) {
              console.warn('Metadata timeout, continuing anyway');
            }
          }

          // ✅ CRITICAL: Retry logic for play
          let playSuccess = false;
          for (let attempt = 0; attempt < 3; attempt++) {
            try {
              await videoRef.current.play();
              console.log('✅ Preview video playing');
              playSuccess = true;
              break;
            } catch (err: any) {
              console.warn(`⚠️ Preview play attempt ${attempt + 1} failed:`, err.message);

              if (attempt < 2) {
                // Wait before retry (exponential backoff)
                await new Promise(resolve => setTimeout(resolve, 100 * Math.pow(2, attempt)));
              }
            }
          }

          if (!playSuccess) {
            console.warn('⚠️ Preview autoplay blocked after all retries - video will show but paused');
          }
        } else if (videoRef.current && !hasVideo) {
          // Audio-only mode - clear video element
          videoRef.current.srcObject = null;
        }

        // Apply initial states
        stream.getAudioTracks().forEach(t => (t.enabled = micOn));
        stream.getVideoTracks().forEach(t => (t.enabled = camOn));

        // Always set mediaReady to true if we got ANY stream (even audio-only or video-only)
        setMediaReady(true);

        // Clear error only if we got both audio and video (or user has them toggled off)
        const hasRequestedVideo = camOn;
        const hasRequestedAudio = micOn;
        const gotVideo = stream.getVideoTracks().length > 0;
        const gotAudio = stream.getAudioTracks().length > 0;

        if ((hasRequestedVideo && gotVideo) || !hasRequestedVideo) {
          if ((hasRequestedAudio && gotAudio) || !hasRequestedAudio) {
            setError(''); // Clear error only if we got everything we requested
          }
        }
      } catch (err: any) {
        console.error('Preview initialization failed:', err);

        // Still allow joining even if media fails - set mediaReady to true
        setMediaReady(true);

        if (err.name === 'NotAllowedError') {
          setError('Camera/microphone access denied. You can still join with limited functionality.');
        } else if (err.name === 'NotFoundError') {
          setError('No camera or microphone found. You can still join the call.');
        } else {
          setError('Could not access camera/microphone. You can still join the call.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    initPreview();

    return () => {
      mounted = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, [selectedVideoDevice, selectedAudioDevice]);

  // ✅ REMOVED: Prejoin lobby should NOT rotate
  // The prejoin view should match the host's PIP view, which doesn't rotate
  // PIP always shows the video in portrait orientation with mirroring for front camera
  // This ensures what the host sees in prejoin matches what they'll see in PIP during the call

  // Toggle controls
  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach(t => (t.enabled = micOn));
    }
  }, [micOn]);

  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.getVideoTracks().forEach(t => (t.enabled = camOn));
    }
  }, [camOn]);

  // Verify customer's last 4 digits
  const handleVerifyPhone = async () => {
    if (!last4Digits.trim() || last4Digits.length !== 4) {
      setError('Please enter exactly 4 digits');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const BASE_URL = import.meta.env.VITE_SIGNALING_SERVER_URL ?? 'http://localhost:3000';
      
      const response = await fetch(`${BASE_URL}/api/auth/verify-customer-phone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomId,
          last4Digits: last4Digits.trim()
        })
      });

      const data = await response.json();

      if (data.ok && data.verified) {
        setPhoneVerified(true);
        setDisplayName(data.customerName || 'Guest');
        // Store JWT token for guest authentication
        if (data.token) {
          setGuestToken(data.token);
          console.log('✅ Guest JWT token received and stored');
        }
        setError('');
      } else {
        setError(data.error || 'Verification failed');
        setPhoneVerified(false);
      }
    } catch (err) {
      console.error('Phone verification error:', err);
      setError('Unable to verify. Please try again.');
      setPhoneVerified(false);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleJoinCall = async () => {
    // Host doesn't need phone verification but MUST be authenticated
    if (isHost) {
      if (!displayName.trim()) {
        setError('Please enter your name');
        return;
      }

      // ✅ SECURITY: Verify host is authenticated before allowing join
      setIsLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();

        // Check for mobile WebView injected session (mobile app injects this)
        const mobileSession = (window as any).__MOBILE_SUPABASE_SESSION__;

        if (session) {
          console.log('✅ Host authenticated via Supabase session:', session.user.id);
        } else if (mobileSession?.access_token) {
          // ✅ MOBILE WEBVIEW: Host is authenticated via mobile app's injected session
          console.log('✅ Host authenticated via mobile WebView session:', mobileSession.user?.id);
        } else if (isWebView()) {
          // WebView but no session - this shouldn't happen if mobile app injected correctly
          console.warn('⚠️ WebView host without session - proceeding anyway (mobile app should have injected session)');
        } else {
          // Not WebView and no session - redirect to login
          setError('Please log in to join as host');
          const returnUrl = encodeURIComponent(window.location.href);
          navigate(`/login?returnUrl=${returnUrl}`);
          return;
        }
      } catch (err) {
        console.error('Auth check failed:', err);
        // In WebView, proceed anyway since mobile app handles auth
        if (!isWebView()) {
          setError('Authentication check failed. Please try again.');
          return;
        }
        console.warn('⚠️ WebView auth check failed - proceeding anyway');
      } finally {
        setIsLoading(false);
      }
    }

    // Customer needs phone verification
    if (!isHost && !phoneVerified) {
      setError('Please verify your phone number first');
      return;
    }

    if (!roomId) {
      setError('Invalid room ID');
      return;
    }

    // Navigate to call with prejoin state
    const targetPath = isHost ? `/video-call/${roomId}` : `/guest/${roomId}`;

    navigate(targetPath, {
      state: {
        prejoin: {
          displayName: displayName.trim(),
          micOn,
          camOn,
          videoDeviceId: selectedVideoDevice,
          audioDeviceId: selectedAudioDevice,
          autoStartRecording: isHost ? autoStartRecording : undefined, // Pass recording preference for hosts
          timestamp: Date.now(), // Unique timestamp to force VideoCallRoom remount on rejoin
          isHost, // Pass host status for proper routing
        },
        guestToken: !isHost ? guestToken : undefined, // Pass JWT token for guests
      },
    });
  };

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Handle back button navigation
  const handleBackButton = () => {
    console.log('[PreJoinLobby] Back button clicked', {
      isMobile,
      isWebView: isWebView(),
      historyLength: window.history.length
    });

    // Check if running in mobile browser (not WebView/app)
    const isMobileBrowser = isMobile && !isWebView();

    if (isMobileBrowser) {
      // Mobile browser - try to close the window/tab
      console.log('[PreJoinLobby] Mobile browser detected - attempting to close window');

      // Attempt to close (this only works if window was opened by script)
      window.close();

      // Most browsers block window.close() for security, so provide alternative
      // If we're still here after a brief delay, navigate to a blank page
      setTimeout(() => {
        window.location.href = 'about:blank';
      }, 100);
    } else if (isWebView()) {
      // WebView (mobile app) - send message to close WebView
      console.log('[PreJoinLobby] WebView detected - sending close message');

      if (typeof (window as any).ReactNativeWebView !== 'undefined') {
        (window as any).ReactNativeWebView.postMessage(JSON.stringify({ type: 'CLOSE_WEBVIEW' }));
      } else if ((window as any).__IS_MOBILE_WEBVIEW__) {
        // Alternative: trigger browser back if mobile app manages history
        window.history.back();
      } else {
        // Fallback: navigate to dashboard
        navigate('/dashboard', { replace: true });
      }
    } else {
      // Web app - navigate to dashboard
      console.log('[PreJoinLobby] Web app detected - navigating to dashboard');
      navigate('/dashboard', { replace: true });
    }
  };

  // If user is too early for scheduled call, show early arrival message
  // NOTE: This check is currently disabled since we removed direct Supabase queries
  // The backend API should handle call timing validation
  if (isTooEarly) {
    return (
      <div
        className="bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4"
        style={{
          minHeight: 'var(--app-vh, 100vh)',
        }}
      >
        <div className="w-full max-w-2xl mx-auto text-center space-y-8">
          {/* Clock Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center shadow-xl">
              <Clock className="w-14 h-14 text-white" />
            </div>
          </div>

          {/* Message */}
          <div className="space-y-4 px-4">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 tracking-tight">
              You're a Bit Early!
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-md mx-auto">
              Your call is scheduled for:
            </p>
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-blue-100 max-w-lg mx-auto">
              <p className="text-2xl font-semibold text-[hsl(var(--wlkthru-purple))]">
                Please wait for the scheduled time
              </p>
            </div>
            <p className="text-base text-gray-500 pt-4">
              We'll send you a reminder message 5 minutes before the call starts.
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 max-w-lg mx-auto">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">Tip:</span> You can join the call up to 5 minutes before the scheduled time.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4"
      style={{
        minHeight: 'var(--app-vh, 100vh)',
      }}
    >
      <div className="w-full max-w-4xl">
        {/* Header with Back Button for Host */}
        <div className="text-center mb-8 relative">
          {isHost && !isMobile && !isWebView() && (
            <Button
              variant="ghost"
              onClick={handleBackButton}
              className="absolute left-0 top-0"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </Button>
          )}
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            {isHost ? 'Host Video Call' : 'Join Video Call'}
          </h1>
          <p className="text-gray-600">Set up your audio and video before joining</p>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
          <div className="grid md:grid-cols-2 gap-0">
            {/* Video Preview - Larger size after auto-record box was shrunk */}
            <div className="relative bg-gray-900 aspect-[4/3] md:aspect-auto md:min-h-[480px] flex items-center justify-center overflow-hidden">
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                controls={false}
                disablePictureInPicture
                disableRemotePlayback
                controlsList="nodownload nofullscreen noremoteplayback"
                className="absolute inset-0"
                style={{
                  // Match PIP behavior: No rotation, only mirroring for front camera
                  // This ensures prejoin preview matches what host sees in PIP during call
                  width: '100%',
                  height: '100%',
                  // Mirror front camera only (like PIP does)
                  transform: cameraFacingMode === 'user' ? 'scaleX(-1)' : 'none',
                  // Always use contain (like PIP)
                  objectFit: 'contain',
                  pointerEvents: 'none'
                }}
                // @ts-ignore - webkit specific attributes
                webkit-playsinline="true"
                x5-playsinline="true"
                x5-video-player-type="h5"
                x5-video-player-fullscreen="false"
              />
              
              {!camOn && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
                      <VideoOff className="h-10 w-10 text-gray-400" />
                    </div>
                    <p className="text-gray-400">Camera is off</p>
                  </div>
                </div>
              )}

              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80 backdrop-blur-sm">
                  <div className="text-center text-white">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-3"></div>
                    <p>Initializing devices...</p>
                  </div>
                </div>
              )}

              {/* Preview Controls */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-3">
                <button
                  onClick={() => setMicOn(!micOn)}
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all shadow-lg ${
                    micOn 
                      ? 'bg-white/20 backdrop-blur-sm hover:bg-white/30' 
                      : 'bg-red-500 hover:bg-red-600'
                  }`}
                >
                  {micOn ? (
                    <Mic className="h-5 w-5 text-white" strokeWidth={2} />
                  ) : (
                    <MicOff className="h-5 w-5 text-white" strokeWidth={2} />
                  )}
                </button>

                <button
                  onClick={() => setCamOn(!camOn)}
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all shadow-lg ${
                    camOn 
                      ? 'bg-white/20 backdrop-blur-sm hover:bg-white/30' 
                      : 'bg-red-500 hover:bg-red-600'
                  }`}
                >
                  {camOn ? (
                    <Video className="h-5 w-5 text-white" strokeWidth={2} />
                  ) : (
                    <VideoOff className="h-5 w-5 text-white" strokeWidth={2} />
                  )}
                </button>

                <button
                  onClick={() => setShowDeviceSettings(!showDeviceSettings)}
                  className="w-12 h-12 rounded-full flex items-center justify-center bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all shadow-lg"
                >
                  <Settings className="h-5 w-5 text-white" strokeWidth={2} />
                </button>
              </div>
            </div>

            {/* Join Form */}
            <div className="p-8 md:p-10 flex flex-col justify-center">
              <div className="space-y-6">
                {/* Customer Last 4 Digits Verification */}
                {!isHost && !phoneVerified && (
                  <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 text-blue-900">
                
                      <h3 className="font-semibold">Verify Your Identity</h3>
                    </div>
                    <p className="text-sm text-blue-800">
                      Enter the last 4 digits of your phone number.
                    </p>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={4}
                        value={last4Digits}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          setLast4Digits(value);
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && last4Digits.length === 4) {
                            handleVerifyPhone();
                          }
                        }}
                        placeholder="Last 4 digits"
                        className="flex-1 text-center text-2xl tracking-widest font-mono"
                        style={{ fontSize: '24px' }}
                        disabled={isVerifying}
                      />
                      <Button
                        onClick={handleVerifyPhone}
                        disabled={isVerifying || last4Digits.length !== 4}
                        className="bg-primary hover:bg-primary/90"
                      >
                        {isVerifying ? 'Verifying...' : 'Verify'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Phone Verified - Show Success */}
                {!isHost && phoneVerified && (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200 flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-green-900">Phone Verified</p>
                      <p className="text-sm text-green-800">Ready to join as {displayName}</p>
                    </div>
                  </div>
                )}

                {/* Host Name Input */}
                {isHost && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Name
                    </label>
                    <Input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleJoinCall()}
                      placeholder="Enter your name"
                      className="w-full px-4 py-3 text-base border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                      style={{ fontSize: '16px' }}
                      autoFocus={!isMobile}
                    />
                  </div>
                )}

                {/* Device Settings */}
                {showDeviceSettings && (
                  <div className="space-y-4 animate-in slide-in-from-top-2">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Camera
                      </label>
                      <select
                        value={selectedVideoDevice}
                        onChange={(e) => setSelectedVideoDevice(e.target.value)}
                        className="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                        style={{ fontSize: '16px' }}
                      >
                        {devices.videoDevices.map(device => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label || `Camera ${device.deviceId.slice(0, 8)}`}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Microphone
                      </label>
                      <select
                        value={selectedAudioDevice}
                        onChange={(e) => setSelectedAudioDevice(e.target.value)}
                        className="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                        style={{ fontSize: '16px' }}
                      >
                        {devices.audioDevices.map(device => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                    {error}
                  </div>
                )}

                {/* Recording Toggle - Host Only (Compact) */}
                {isHost && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 rounded-lg border border-purple-200">
                    <input
                      type="checkbox"
                      id="recording-checkbox"
                      checked={autoStartRecording}
                      onChange={(e) => setAutoStartRecording(e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 focus:ring-1 cursor-pointer"
                    />
                    <label htmlFor="recording-checkbox" className="text-sm text-purple-900 cursor-pointer">
                      <span className="font-medium">Auto-start recording</span>
                      <span className="text-purple-600 ml-1 text-xs">(both parties notified)</span>
                    </label>
                  </div>
                )}

                {/* Terms and Conditions Consent */}
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <input
                    type="checkbox"
                    id="terms-checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                  />
                  <label htmlFor="terms-checkbox" className="flex-1 text-sm text-gray-700 cursor-pointer">
                    I agree to the{' '}
                    <a
                      href="/legal?section=terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 underline font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Terms of Service
                    </a>
                    {' '}and{' '}
                    <a
                      href="/legal?section=privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 underline font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Privacy Policy
                    </a>
                    .
                  </label>
                </div>

                <Button
                  onClick={handleJoinCall}
                  disabled={
                    (!isHost && !phoneVerified) ||
                    (isHost && !displayName.trim()) ||
                    !mediaReady ||
                    !termsAccepted
                  }
                  className="w-full py-6 text-lg font-semibold bg-primary hover:bg-primary/90 active:bg-primary/80 disabled:bg-gray-300 rounded-xl shadow-lg transition-all disabled:cursor-not-allowed"
                >
                  Join Call
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}